// De blokken van Chat 2.0 (ontwerp-artifact 26-08, §3-§5; ADR
// 2026-08-26-chat-2-0-bouw).
//
// De vormregel uit dat ontwerp, hier in code: LIJSTJES zijn de standaard in
// het antwoord (vaardigheden als genummerd top-5, regelingen met statusbadge),
// een KAART is voor één centrale entiteit, een BRON blijft altijd een pil, en
// het telgetal woont in een TELKAART — nooit los in de tekst ("dat ziet er
// rommelig uit", owner 26-08). Elke rij opent dezelfde slide-in (ChatPaneel).
//
// EERLIJKHEIDSREGELS, geërfd van de lanen eronder:
//  - een kaart toont alleen velden die de API echt droeg;
//  - de uitlichtscore staat nooit zonder zijn reden-badges (contractregel);
//  - een grafiek verschijnt alleen bij >= 2 meetpunten;
//  - een regeling zonder bedrag heet "geen bedrag op de pagina";
//  - de doorzocht-regel noemt alleen corpora die dit antwoord écht doorzocht.

import { type ReactNode, useState } from "react";

import type { ChatResponse, ChatStap, KaartLanding } from "../contracts";
import { color, radius, size, weight } from "./chatTokens";
import { BewijsBadge, Bewijsbalk, isGezagBadge } from "./Bewijs";
import { GeteldBadge, Toolstappen, bonnetjeDelen, naarLanding, type Telling } from "./ChatBonnetje";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";

export type Kaart = NonNullable<ChatResponse["kaarten"]>[number];
export type Reeks = NonNullable<ChatResponse["reeksen"]>[number];
export type Zoekwerk = NonNullable<ChatResponse["zoekwerk"]>;

/** Wat een klik op een kaart of lijstrij opent (de in-chat slide-in).
 *  "ontwikkeling" alleen bij aan-stand van de trendlaag (CH-A: een klik op een
 *  ontwikkeling of op haar nummer opent de ontwikkelingenkaart); `titel` staat
 *  dan in de kop zolang de kaart laadt. */
export type PaneelDoel = {
  soort: "skill" | "trend" | "funding" | "bron" | "ontwikkeling";
  id: string;
  titel?: string;
};

const nf = new Intl.NumberFormat("nl-NL");

/** De soort-stip: één vaste kleur per entiteitssoort, verder blijft alles
 *  monochroom — de stip is herkenning, geen kermis. */
const STIP: Record<string, string> = {
  skill: color.accentGreen,
  trend: color.accentBlue,
  funding: color.accentAmber,
};

export function Stip({ soort }: { soort: string }) {
  return (
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: radius.pill,
        background: STIP[soort] ?? color.ink,
        flexShrink: 0,
      }}
    />
  );
}

export function Bloklabel({ tekst, extra }: { tekst: string; extra?: string }) {
  return (
    <div
      style={{
        fontSize: size.label,
        fontWeight: weight.medium,
        color: color.faint,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        marginBottom: 7,
      }}
    >
      {tekst}
      {extra ? (
        <span style={{ textTransform: "none", letterSpacing: 0, color: color.accentAmber }}>
          {" · "}
          {extra}
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * De doorzocht-regel (opnamepunt P7): altijd aanwezig, altijd dezelfde
 * volgorde — doorzocht → gelezen → geciteerd → stappen. De grote getallen
 * staan bewust vet: dit is óók het moment om de omvang van het corpus te
 * laten zien. "Toon stappen" klapt de bestaande stappenlijst uit.
 * ------------------------------------------------------------------------- */
export function ZoekRegel({
  zoekwerk,
  stappen,
  geciteerd,
  opgehaald = 0,
  laag,
}: {
  zoekwerk?: Zoekwerk | null;
  stappen: ChatStap[];
  /** Alleen bij aan-stand van de trendlaag (CH-A): de trends die de tools
   *  tegenkwamen en die het antwoord koos. Dan wordt de regel het bonnetje
   *  "N stappen nagekeken: zoekt · weegt · kiest · telt"; de doorzocht-getallen
   *  staan dan in het uitgeklapte deel. Afwezig = de regel zoals live. */
  laag?: { gewogen: number; gekozen: number };
  /** Unieke bronmarkers ín de antwoordtekst — dezelfde telling als de
   *  bronnenrij "Geciteerd in de tekst · N". Tot 27-08 stond hier
   *  citations.length en heetten twee verschillende tellingen allebei
   *  "geciteerd" (feedbackronde, B1). */
  geciteerd: number;
  /** Alle unieke opgehaalde documenten (citations.length). */
  opgehaald?: number;
}) {
  const [open, setOpen] = useState(false);
  const stapTotaal = stappen.filter((s) => s.soort === "tool_klaar").length;
  if (!zoekwerk && stapTotaal === 0) return null;

  const corpora: string[] = [];
  if (zoekwerk?.fragmenten) {
    corpora.push(
      `${nf.format(zoekwerk.fragmenten)} fragmenten` +
        (zoekwerk.documenten ? ` uit ${nf.format(zoekwerk.documenten)} documenten` : ""),
    );
  }
  // Trendlaag (schakelaar aan): de trendzoek doorzoekt bevindingen.
  if (zoekwerk?.trends) corpora.push(`${nf.format(zoekwerk.trends)} ${trendlaagAan() ? "bevindingen" : "trends"}`);
  if (zoekwerk?.vaardigheden) corpora.push(`${nf.format(zoekwerk.vaardigheden)} vaardigheden`);
  if (zoekwerk?.observaties) corpora.push(`${nf.format(zoekwerk.observaties)} waarnemingen`);

  const delen: ReactNode[] = [];
  if (corpora.length) {
    delen.push(
      <span key="c">
        <b style={{ color: color.ink, fontWeight: weight.bold, fontVariantNumeric: "tabular-nums" }}>
          {corpora.join(" · ")}
        </b>{" "}
        doorzocht
      </span>,
    );
  } else {
    delen.push(<span key="c">telling in de database</span>);
  }
  if (zoekwerk?.gelezen) delen.push(<span key="g">{zoekwerk.gelezen} gelezen</span>);
  if (opgehaald > 0)
    delen.push(<span key="op">{opgehaald} {opgehaald === 1 ? "bron" : "bronnen"} opgehaald</span>);
  if (geciteerd > 0) delen.push(<span key="ci">{geciteerd} in de tekst geciteerd</span>);
  if (stapTotaal > 0)
    delen.push(
      <span key="s">
        {stapTotaal} {stapTotaal === 1 ? "stap" : "stappen"}
      </span>,
    );

  if (laag) {
    // CH-A: het bonnetje als één rustige regel boven het antwoord; uitklappen
    // toont wat er doorzocht is en het volledige stappenverslag.
    const stapDelen = bonnetjeDelen(stappen, laag);
    const doorzocht = delen.filter((d) => (d as { key?: unknown }).key !== "s");
    return (
      <div data-testid="bonnetje-trendlaag" style={{ margin: "0 0 8px", maxWidth: "82%" }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          disabled={stapTotaal === 0}
          style={{
            display: "flex", alignItems: "baseline", gap: 6, background: "none", border: "none", padding: "2px 0",
            font: "inherit", fontSize: 12, lineHeight: 1.5, color: color.faint, textAlign: "left",
            cursor: stapTotaal > 0 ? "pointer" : "default",
          }}
        >
          <span style={{ color: color.accentGreen, fontWeight: weight.bold, flexShrink: 0 }}>✓</span>
          <span>
            {stapTotaal > 0 ? `${stapTotaal} ${stapTotaal === 1 ? "stap" : "stappen"} nagekeken` : "Nagekeken"}
            {stapDelen.length > 0 ? `: ${stapDelen.join(" · ")}` : ""}
          </span>
          {stapTotaal > 0 && <span style={{ color: color.disabled, flexShrink: 0 }}>{open ? "▴" : "▾"}</span>}
        </button>
        {open && (
          <div style={{ padding: "4px 0 0 18px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, fontSize: 11.5, color: color.soft }}>
              {doorzocht.map((d, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  {i > 0 && <span style={{ color: color.disabled }}>·</span>}
                  {d}
                </span>
              ))}
            </div>
            <Toolstappen stappen={stappen} bezig={false} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ margin: "2px 0 10px", maxWidth: "82%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          flexWrap: "wrap",
          fontSize: 12,
          color: color.soft,
          border: `1px solid ${color.lineSoft}`,
          borderRadius: radius.block,
          background: color.surfaceAlt,
          padding: "7px 12px",
        }}
      >
        <span style={{ color: color.accentGreen, fontWeight: weight.bold }}>✓</span>
        {delen.map((d, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            {i > 0 && <span style={{ color: color.disabled }}>·</span>}
            {d}
          </span>
        ))}
        {stapTotaal > 0 && (
          <button
            onClick={() => setOpen(!open)}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontSize: 11.5,
              color: color.faint,
              cursor: "pointer",
              textDecoration: "underline",
              whiteSpace: "nowrap",
            }}
          >
            {open ? "verberg stappen" : "toon stappen"}
          </button>
        )}
      </div>
      {open && (
        <div style={{ padding: "2px 12px 0" }}>
          <Toolstappen stappen={stappen} bezig={false} />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * De telkaart: het grote getal woont in een kaart, nooit los in de tekst
 * (owner 26-08). De geteld-badge en de tab-afslag zitten ín de kaart.
 * ------------------------------------------------------------------------- */
const TEL_DING: Record<string, string> = {
  trends: "trends",
  skills: "vaardigheden",
  bronnen: "bronnen",
  observaties: "waarnemingen",
};
const TEL_STIP: Record<string, string> = {
  trends: color.accentBlue,
  skills: color.accentGreen,
  bronnen: color.soft,
  observaties: color.ink,
};

export function TelKaart({
  telling,
  onOpen,
}: {
  telling: Telling;
  onOpen?: (landing: KaartLanding) => void;
}) {
  const landing = naarLanding(telling);
  const ding = TEL_DING[telling.subject] ?? telling.subject;
  const opDeTab = typeof telling.op_de_tab === "number" ? telling.op_de_tab : telling.aantal;
  const sub: string[] = [];
  if (typeof telling.regels === "number" && telling.regels !== telling.aantal)
    sub.push(`${nf.format(telling.regels)} regels in de lijstweergave`);
  if (typeof telling.op_de_tab === "number" && telling.op_de_tab !== telling.aantal)
    sub.push(`${nf.format(telling.op_de_tab)} op de Trends-tab (het kernbeeld)`);
  return (
    <div
      style={{
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        background: color.surface,
        padding: "13px 16px",
        margin: "4px 0 12px",
        maxWidth: 420,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          fontWeight: weight.bold,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: color.faint,
          marginBottom: 6,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: radius.pill,
            background: TEL_STIP[telling.subject] ?? color.ink,
          }}
        />
        Telling · hele database
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span
          style={{
            fontSize: 32,
            fontWeight: weight.heavy,
            color: color.ink,
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {nf.format(telling.aantal)}
        </span>
        <GeteldBadge telling={telling} />
      </div>
      {sub.length > 0 && (
        <div style={{ fontSize: 12, color: color.soft, marginTop: 4 }}>{sub.join(" · ")}</div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `1px solid ${color.lineFaint}`,
          marginTop: 10,
          paddingTop: 8,
          fontSize: 11.5,
          color: color.soft,
          gap: 10,
        }}
      >
        <span>{ding}</span>
        {landing && onOpen && opDeTab > 0 ? (
          <button
            onClick={() => onOpen(landing)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontSize: 12,
              fontWeight: weight.medium,
              color: color.ink,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Bekijk alle {nf.format(opDeTab)} →
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Gedeelde lijstrij: nummer/stip + naam + kerncijfers rechts + chevron.
 * ------------------------------------------------------------------------- */
export function Lijst({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        border: `1px solid ${color.lineSoft}`,
        borderRadius: radius.card,
        overflow: "hidden",
        background: color.surface,
      }}
    >
      {children}
    </div>
  );
}

function LijstRij({
  rang,
  soort,
  naam,
  naamExtra,
  rechts,
  laatste,
  onKlik,
}: {
  rang?: number;
  soort: string;
  naam: string;
  naamExtra?: string;
  rechts: ReactNode;
  laatste: boolean;
  onKlik?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onKlik}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        width: "100%",
        background: hover ? color.surfaceAlt : "none",
        border: "none",
        borderBottom: laatste ? "none" : `1px solid ${color.lineFaint}`,
        padding: "9px 14px",
        font: "inherit",
        fontSize: 13,
        textAlign: "left",
        cursor: onKlik ? "pointer" : "default",
        transition: "background 0.12s",
      }}
    >
      {typeof rang === "number" && (
        <span
          style={{
            fontSize: 11.5,
            fontWeight: weight.medium,
            color: color.faint,
            width: 14,
            textAlign: "right",
            flexShrink: 0,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {rang}
        </span>
      )}
      <Stip soort={soort} />
      <span
        style={{
          fontWeight: weight.medium,
          color: color.ink,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {naam}
        {naamExtra ? (
          <span style={{ fontWeight: weight.regular, color: color.faint }}> · {naamExtra}</span>
        ) : null}
      </span>
      <span
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {rechts}
        <span style={{ color: hover ? color.ink : color.disabled, fontWeight: weight.medium }}>
          →
        </span>
      </span>
    </button>
  );
}

function MiniBadge({
  tekst,
  toon,
}: {
  tekst: string;
  toon: "groen" | "amber" | "grijs";
}) {
  const kleur =
    toon === "groen"
      ? { bg: "#ecfdf5", fg: color.accentGreen }
      : toon === "amber"
        ? { bg: color.accentAmberBg, fg: color.accentAmber }
        : { bg: color.lineFaint, fg: color.muted };
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: weight.bold,
        borderRadius: radius.pill,
        padding: "2px 8px",
        background: kleur.bg,
        color: kleur.fg,
        whiteSpace: "nowrap",
      }}
    >
      {tekst}
    </span>
  );
}

export function KV({ label, waarde }: { label: string; waarde: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        color: color.soft,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {label ? `${label} ` : ""}
      <b style={{ color: color.ink, fontWeight: weight.bold }}>{waarde}</b>
    </span>
  );
}

const nl1 = (n: number) => n.toLocaleString("nl-NL", { maximumFractionDigits: 1 });

/* ------------------------------------------------------------------------- *
 * Het genummerde top-5-vaardighedenlijstje, mét de vaste knop naar de
 * Skills-tab (owner 26-08: "altijd een knop, idealiter met een
 * voorgeprogrammeerde selectie"). De selectie reist als voorzet-filter mee.
 * ------------------------------------------------------------------------- */
export function SkillLijst({
  kaarten,
  onOpenKaart,
  onOpen,
}: {
  kaarten: Kaart[];
  onOpenKaart: (doel: PaneelDoel) => void;
  onOpen?: (landing: KaartLanding) => void;
}) {
  if (kaarten.length === 0) return null;
  const titel =
    kaarten.length === 1
      ? "De best passende vaardigheid"
      : `De ${kaarten.length} best passende vaardigheden`;
  return (
    <div style={{ margin: "12px 0" }}>
      <Bloklabel tekst={titel} />
      <Lijst>
        {kaarten.map((k, i) => (
          <LijstRij
            key={k.id}
            rang={k.rang ?? i + 1}
            soort="skill"
            naam={k.titel}
            laatste={i === kaarten.length - 1}
            onKlik={() => onOpenKaart({ soort: "skill", id: k.id })}
            rechts={
              <>
                {k.quickWin && <MiniBadge tekst="Quick win" toon="groen" />}
                {typeof k.prioriteit === "number" && (
                  <KV label="prio" waarde={nl1(k.prioriteit)} />
                )}
              </>
            }
          />
        ))}
      </Lijst>
      {onOpen && (
        <button
          onClick={() =>
            onOpen({
              soort: "tab",
              tab: "skills",
              filter: {
                selectie: kaarten.map((k) => ({ id: k.id, titel: k.titel })),
              },
            })
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            border: `1px solid ${color.border}`,
            borderRadius: radius.block,
            background: color.surfaceAlt,
            padding: "8px 12px",
            marginTop: 8,
            font: "inherit",
            fontSize: 12.5,
            fontWeight: weight.medium,
            color: color.ink,
            cursor: "pointer",
          }}
        >
          Bekijk deze {kaarten.length} op de Skills-tab →
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Het regelingenlijstje. Erft de eerlijkheidsregels van de funding-lane:
 * statusbadge, "geen bedrag op de pagina", en de vaste voetnoot dat er geen
 * gemeten koppeling met vaardigheden bestaat.
 * ------------------------------------------------------------------------- */
function statusToon(status?: string): "groen" | "amber" | "grijs" {
  if (status === "open") return "groen";
  if (status === "aangekondigd") return "amber";
  return "grijs";
}

export function RegelingenLijst({
  kaarten,
  onOpenKaart,
  voetnoot,
  kop,
}: {
  kaarten: Kaart[];
  onOpenKaart: (doel: PaneelDoel) => void;
  /** ADDITIEF (W5): een eigen voetnoot, bv. in het skill-detail. Zonder deze
   *  prop blijft de vaste chatvoetnoot staan. */
  voetnoot?: ReactNode;
  /** ADDITIEF (W5): een eigen bloklabel; standaard "Regelingen · N". */
  kop?: string;
}) {
  if (kaarten.length === 0) return null;
  return (
    <div style={{ margin: "12px 0" }}>
      <Bloklabel tekst={kop ?? `Regelingen · ${kaarten.length}`} />
      <Lijst>
        {kaarten.map((k, i) => (
          <LijstRij
            key={k.id}
            soort="funding"
            naam={k.titel}
            naamExtra={k.verstrekker}
            laatste={i === kaarten.length - 1}
            onKlik={() => onOpenKaart({ soort: "funding", id: k.id })}
            rechts={
              <>
                {k.status && <MiniBadge tekst={k.status} toon={statusToon(k.status)} />}
                {/* Review fase 6: het voorbehoud van een vondst op de kaart zelf. */}
                {k.nagekeken === false && (
                  <MiniBadge tekst="nieuw, nog niet nagekeken" toon="amber" />
                )}
                {k.maxBedrag ? (
                  <KV label="" waarde={k.maxBedrag} />
                ) : (
                  <span style={{ fontSize: 11, color: color.faint, whiteSpace: "nowrap" }}>
                    geen bedrag op de pagina
                  </span>
                )}
              </>
            }
          />
        ))}
      </Lijst>
      <div style={{ fontSize: 11, color: color.faint, marginTop: 5 }}>
        {voetnoot ?? (
          <>
            selectie op sector en thema · er bestaat geen gemeten koppeling tussen een
            regeling en een vaardigheid
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Trends: één centrale entiteit = kaart, meerdere = lijstje (vormregel C4).
 * De uitlichtscore staat nooit zonder zijn badges.
 * ------------------------------------------------------------------------- */
export function TrendBlok({
  kaarten,
  onOpenKaart,
}: {
  kaarten: Kaart[];
  onOpenKaart: (doel: PaneelDoel) => void;
}) {
  if (kaarten.length === 0) return null;
  if (kaarten.length === 1) {
    const k = kaarten[0]!;
    const scoreMetBadges = typeof k.uitlichtscore === "number" && (k.badges?.length ?? 0) > 0;
    return (
      <div style={{ margin: "12px 0" }}>
        <Bloklabel tekst="De trend in dit antwoord" />
        <button
          onClick={() => onOpenKaart({ soort: "trend", id: k.id })}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            border: `1px solid ${color.lineSoft}`,
            borderRadius: radius.card,
            background: color.surface,
            padding: "13px 16px",
            font: "inherit",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 10,
              fontWeight: weight.bold,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: color.faint,
              marginBottom: 7,
            }}
          >
            <Stip soort="trend" />
            Trend{k.categorie ? ` · ${k.categorie}` : ""}{k.laag ? ` · ${k.laag}` : ""}
          </div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: weight.bold,
              color: color.ink,
              lineHeight: 1.35,
              marginBottom: 5,
            }}
          >
            {k.titel}
          </div>
          {k.tldr && (
            <div
              style={{
                fontSize: 12,
                color: color.soft,
                lineHeight: 1.5,
                marginBottom: 8,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {k.tldr}
            </div>
          )}
          {/* Methode v3: met bewijs wijkt de gezag-badge ("zwaar rapport
              (autoriteit x)") voor "bevestigd door: …" met het balkje. */}
          {k.bewijs ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              <BewijsBadge bewijs={k.bewijs} />
              <Bewijsbalk bewijs={k.bewijs} klein />
              {(k.badges ?? [])
                .filter((b) => !isGezagBadge(b) && b !== k.bewijs?.tekst)
                .map((b) => (
                  <MiniBadge key={b} tekst={b} toon="grijs" />
                ))}
            </div>
          ) : (k.badges?.length ?? 0) > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
              {k.badges!.map((b) => (
                <MiniBadge key={b} tekst={b} toon="grijs" />
              ))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 14,
              borderTop: `1px solid ${color.lineFaint}`,
              paddingTop: 8,
            }}
          >
            {scoreMetBadges && <KV label="uitgelicht" waarde={nl1(k.uitlichtscore!)} />}
            {typeof k.bronnen === "number" && (
              <KV label="bronnen" waarde={String(k.bronnen)} />
            )}
            {k.level && <KV label="niveau" waarde={k.level} />}
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                fontWeight: weight.medium,
                color: color.ink,
              }}
            >
              Open →
            </span>
          </div>
        </button>
      </div>
    );
  }
  return (
    <div style={{ margin: "12px 0" }}>
      <Bloklabel tekst={`Trends bij dit antwoord · ${kaarten.length}`} />
      <Lijst>
        {kaarten.map((k, i) => (
          <LijstRij
            key={k.id}
            soort="trend"
            naam={k.titel}
            laatste={i === kaarten.length - 1}
            onKlik={() => onOpenKaart({ soort: "trend", id: k.id })}
            rechts={
              <>
                {k.bewijs ? (
                  <BewijsBadge bewijs={k.bewijs} />
                ) : (
                  k.badges?.[0] && <MiniBadge tekst={k.badges[0]} toon="grijs" />
                )}
                {typeof k.bronnen === "number" && (
                  <KV label="bronnen" waarde={String(k.bronnen)} />
                )}
              </>
            }
          />
        ))}
      </Lijst>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Het grafiekblok: huis-SVG in het ReeksWidget-idioom (geen library). De
 * hoofdreeks is de regio (onze regio eerst), Nederland is de stippellijn.
 * Grafiek alleen bij >= 2 punten — een lijn met één punt is een verzonnen
 * beweging.
 * ------------------------------------------------------------------------- */
type Punt = Reeks["punten"][number];

function perSerie(reeks: Reeks): Map<string, Punt[]> {
  const series = new Map<string, Punt[]>();
  for (const p of reeks.punten) {
    const sleutel = p.regio ?? p.sleutel ?? "";
    if (!series.has(sleutel)) series.set(sleutel, []);
    series.get(sleutel)!.push(p);
  }
  for (const punten of series.values()) {
    punten.sort((a, b) => a.periode.localeCompare(b.periode));
  }
  return series;
}

function periodeLabel(periode: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(periode);
  if (!m) return periode;
  const [, jaar, maand] = m;
  if (maand === "01") return jaar!;
  const namen = ["", "jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${namen[Number(maand)]} ${jaar}`;
}

export function ReeksBlok({ reeks }: { reeks: Reeks }) {
  const series = perSerie(reeks);
  const sleutels = [...series.keys()];
  const eigen =
    sleutels.find((s) => /zuidoost|eindhoven|helmond|brainport/i.test(s)) ??
    sleutels.find((s) => !/nederland/i.test(s)) ??
    sleutels[0];
  const hoofd = eigen !== undefined ? (series.get(eigen) ?? []) : [];
  const refSleutel = sleutels.find((s) => /nederland/i.test(s) && s !== eigen);
  const referentie = refSleutel ? (series.get(refSleutel) ?? []) : [];
  if (hoofd.length < 2) return null;

  const alle = [...hoofd, ...(referentie.length >= 2 ? referentie : [])];
  const min = Math.min(...alle.map((p) => p.waarde));
  const max = Math.max(...alle.map((p) => p.waarde));
  const span = max - min || 1;
  const W = 560;
  const H = 130;
  const PAD = 8;
  const x = (i: number, n: number) => PAD + (i / Math.max(1, n - 1)) * (W - 2 * PAD);
  const y = (w: number) => 14 + (1 - (w - min) / span) * (H - 34);
  const lijn = (punten: Punt[]) =>
    punten.map((p, i) => `${x(i, punten.length).toFixed(1)},${y(p.waarde).toFixed(1)}`).join(" ");

  const laatste = hoofd[hoofd.length - 1]!;
  const eerste = hoofd[0]!;
  const isProcent = /%/.test(reeks.eenheid ?? "");
  let delta = "";
  if (isProcent) {
    delta = `${laatste.waarde - eerste.waarde >= 0 ? "+" : ""}${nl1(laatste.waarde - eerste.waarde)} pp sinds ${periodeLabel(eerste.periode)}`;
  } else if (eerste.waarde !== 0) {
    const pct = ((laatste.waarde - eerste.waarde) / Math.abs(eerste.waarde)) * 100;
    delta = `${pct >= 0 ? "+" : ""}${nl1(pct)}% sinds ${periodeLabel(eerste.periode)}`;
  }

  return (
    <div style={{ margin: "12px 0" }}>
      <div
        style={{
          border: `1px solid ${color.lineSoft}`,
          borderRadius: radius.card,
          background: color.surface,
          padding: "14px 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: weight.bold, color: color.ink }}>
            {reeks.indicator.replace(/_/g, " ")}
            {eigen ? ` · ${eigen}` : ""}
          </span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: 22,
              fontWeight: weight.bold,
              color: color.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {nl1(laatste.waarde)}
          </span>
        </div>
        {delta && (
          <div style={{ fontSize: 11.5, color: color.soft, marginTop: 2 }}>{delta}</div>
        )}
        <svg
          viewBox={`0 0 ${W} ${H + 16}`}
          role="img"
          aria-label={`Reeks ${reeks.indicator}, ${hoofd.length} meetpunten`}
          style={{ width: "100%", height: "auto", display: "block", marginTop: 8 }}
        >
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1={0}
              y1={14 + f * (H - 34)}
              x2={W}
              y2={14 + f * (H - 34)}
              style={{ stroke: color.lineSoft }}
            />
          ))}
          {referentie.length >= 2 && (
            <polyline
              fill="none"
              style={{ stroke: color.faint }}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              points={lijn(referentie)}
            />
          )}
          <polyline fill="none" style={{ stroke: color.ink }} strokeWidth={2} points={lijn(hoofd)} />
          <circle
            cx={x(hoofd.length - 1, hoofd.length)}
            cy={y(laatste.waarde)}
            r={3.2}
            style={{ fill: color.ink }}
          />
          <text x={PAD} y={H + 10} fontSize={10} style={{ fill: color.faint }}>
            {periodeLabel(eerste.periode)}
          </text>
          <text x={W - PAD} y={H + 10} fontSize={10} style={{ fill: color.faint }} textAnchor="end">
            {periodeLabel(laatste.periode)}
          </text>
        </svg>
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            fontSize: 10.5,
            color: color.faint,
            marginTop: 6,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 14, borderTop: `2px solid ${color.ink}` }} />
            {eigen || "reeks"}
          </span>
          {referentie.length >= 2 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 14, borderTop: `2px dashed ${color.faint}` }} />
              {refSleutel}
            </span>
          )}
          <span style={{ marginLeft: "auto" }}>
            {reeks.bron ?? ""}
            {reeks.bron ? " · " : ""}
            {hoofd.length} meetpunten
            {reeks.eenheid ? ` · ${reeks.eenheid}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}
